import { CSM_STATE_KEY } from '@/apis/csm-apis';
import { NOTES_STATE_KEY } from '@/apis/notes-apis';
import {
  TASKS_ALL_FILING_STATE_KEY,
  TASKS_BY_FILING_ID_STATE_KEY,
  TASKS_FILING_LIST_STATE_KEY,
  TASKS_FILING_SPECIAL_HANDLING_COUNT_STATE_KEY,
} from '@/apis/tasks-apis';
import {
  addEnabledJurisdiction,
  AVAILABLE_COUNTRIES_STATE_KEY,
  getAdminDowngradeSubscription,
  getAvailableCountries,
  getEnabledJurisdictions,
  getPackagingCapabilities,
  getOrganizations,
  getOrganizationUsers,
  getOrganizationSettings,
  getOrgDetails,
  ORGANIZATIONS_STATE_KEY,
  createPhysicalMailOnboardingBatch,
  removeEnabledJurisdiction,
  type SpecialHandlingPayload,
  turnOffSpecialHandling,
  turnOnSpecialHandling,
  updatePackagingCapability,
  updateOrganizationAdminFields,
  updateOrganizationDetails,
  updateOrganizationSettings,
  updateSpecialHandling,
} from '@/apis/organizations-apis';
import { toaster } from '@/components/ui/toaster-instance';
import {
  AdminSubscriptionDowngradeState,
  CountryOption,
  EnabledJurisdictionsResponse,
  OrganizationAdminUpdate,
  OrganizationDetailsUpdate,
  OrganizationSettingUpdate,
  PackagingCapabilitiesResponse,
  PackagingCapability,
  packagingCapabilitiesResponseSchema,
} from '@/schemas';
import type { Organization } from '@/types/organizations';
import { getApiErrorMessage } from '@/utils/api';
import { getStarterCancelPollInterval, isStarterCancelInProgress } from '@/utils/downgrade-copy';
import type { QueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const organizationByIdKey = (orgId: string) => [ORGANIZATIONS_STATE_KEY, orgId, 'byId'] as const;
const downgradeSubscriptionKey = (orgId: string) =>
  [ORGANIZATIONS_STATE_KEY, orgId, 'downgrade', 'subscription'] as const;

// When each org's in-progress state was first seen, so the 5 minute cap spans both polled reads.
const starterCancelPollStartedAtMs = new Map<string, number>();

/**
 * Shared `refetchInterval` for the organization, subscription and packaging reads. The state needs the plan from
 * one read and the status from the other, so each read looks at the other's cached data.
 */
const getStarterCancelRefetchInterval = (queryClient: QueryClient, orgId: string): number | false => {
  const organization = queryClient.getQueryData<Organization | null>(organizationByIdKey(orgId));
  const subscription = queryClient.getQueryData<AdminSubscriptionDowngradeState | null>(
    downgradeSubscriptionKey(orgId)
  );
  if (!isStarterCancelInProgress(organization?.billing_plan, subscription?.status)) {
    starterCancelPollStartedAtMs.delete(orgId);
    return false;
  }
  if (!starterCancelPollStartedAtMs.has(orgId)) {
    starterCancelPollStartedAtMs.set(orgId, Date.now());
  }
  return getStarterCancelPollInterval(true, starterCancelPollStartedAtMs.get(orgId) ?? null);
};

export const useGetOrgDetailsQuery = (orgId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'details'],
    queryFn: async () => {
      const res = await getOrgDetails(orgId);
      return res?.data || null;
    },
    enabled: enabled && !!orgId,
    // Large orgs can hang past the gateway; never stack retries on this path.
    retry: false,
  });
};

export const useGetOrgDetailsQueryWithReveal = (orgId: string, reveal: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'details', 'reveal', reveal],
    queryFn: async () => {
      const res = await getOrgDetails(orgId, reveal);
      return res?.data || null;
    },
    enabled: enabled && !!orgId,
    retry: false,
  });
};

export const useGetOrganizationByIdQuery = (orgId: string, enabled: boolean = true) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: organizationByIdKey(orgId),
    queryFn: async () => {
      const res = await getOrganizations({
        search_query: orgId,
      });
      const organizations = res?.data?.items || [];
      return organizations.find(org => org.id === orgId) || null;
    },
    enabled,
    refetchInterval: () => getStarterCancelRefetchInterval(queryClient, orgId),
  });
};

export const useGetOrganizationUsersQuery = (orgId: string) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'users'],
    queryFn: async () => {
      const response = await getOrganizationUsers(orgId);
      return response.data.users;
    },
    enabled: Boolean(orgId),
  });
};

export const useCreatePhysicalMailOnboardingBatchMutation = (orgId: string) => {
  return useMutation({
    mutationFn: (customerEmails: string[]) => createPhysicalMailOnboardingBatch(orgId, customerEmails),
  });
};

export const useGetOrganizationSettingsQuery = (orgId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'settings'],
    queryFn: async () => {
      const res = await getOrganizationSettings(orgId);
      return res?.data || null;
    },
    enabled,
  });
};

export const useEnabledJurisdictionsQuery = (
  orgId: string,
  options?: Partial<UseQueryOptions<EnabledJurisdictionsResponse | null, Error>>
) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'enabled-jurisdictions'],
    queryFn: async () => {
      const res = await getEnabledJurisdictions(orgId);
      return res?.data || null;
    },
    enabled: !!orgId,
    ...options,
  });
};

export const useGetAvailableCountriesQuery = (options?: Partial<UseQueryOptions<CountryOption[], Error>>) => {
  return useQuery({
    queryKey: [AVAILABLE_COUNTRIES_STATE_KEY],
    queryFn: async () => {
      const res = await getAvailableCountries();
      return res.data.countries;
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour - this data rarely changes
    ...options,
  });
};

export const useUpdateEnabledJurisdictionMutation = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, enabled }: { code: string; enabled: boolean }) => {
      return enabled ? addEnabledJurisdiction(orgId, code) : removeEnabledJurisdiction(orgId, code);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'enabled-jurisdictions'],
      });
      toaster.create({
        title: 'Success',
        description: 'Enabled jurisdictions updated successfully',
        type: 'success',
      });
    },
    onError: (error: unknown) => {
      const detail =
        typeof error === 'object' && error && 'response' in error
          ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      toaster.create({
        title: 'Error',
        description: detail || 'Failed to update enabled jurisdictions',
        type: 'error',
      });
    },
  });
};

export const usePackagingCapabilitiesQuery = (
  orgId: string,
  options?: Partial<UseQueryOptions<PackagingCapabilitiesResponse | null, Error>>
) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'packaging'],
    queryFn: async () => {
      const res = await getPackagingCapabilities(orgId);
      return res?.data || null;
    },
    // Access Settings follow the plan, so they refresh with the other reads while the plan change is pending.
    refetchInterval: () => getStarterCancelRefetchInterval(queryClient, orgId),
    enabled: !!orgId,
    ...options,
  });
};

export const useAdminDowngradeSubscriptionQuery = (
  orgId: string,
  options?: Partial<UseQueryOptions<AdminSubscriptionDowngradeState | null, Error>>
) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: downgradeSubscriptionKey(orgId),
    queryFn: () => getAdminDowngradeSubscription(orgId),
    refetchInterval: () => getStarterCancelRefetchInterval(queryClient, orgId),
    // Chargebee-backed; avoid stacking retries when the site is unavailable.
    retry: false,
    ...options,
    enabled: !!orgId && (options?.enabled ?? true),
  });
};

export const useUpdatePackagingCapabilityMutation = (orgId: string) => {
  const queryClient = useQueryClient();
  const packagingQueryKey = [ORGANIZATIONS_STATE_KEY, orgId, 'packaging'] as const;
  return useMutation({
    mutationFn: ({ capability, enabled }: { capability: PackagingCapability; enabled: boolean }) =>
      updatePackagingCapability(orgId, capability, enabled),
    onSuccess: response => {
      const parsed = packagingCapabilitiesResponseSchema.safeParse(response.data);
      if (parsed.success) {
        queryClient.setQueryData(packagingQueryKey, parsed.data);
      }
      void queryClient.invalidateQueries({ queryKey: packagingQueryKey });
      toaster.create({
        title: 'Success',
        description: 'Access settings updated successfully',
        type: 'success',
      });
    },
    onError: (error: unknown) => {
      // The flag may already be saved when follow-up work fails. Refresh so
      // the switch matches that stored value instead of the pre-click one.
      void queryClient.invalidateQueries({ queryKey: packagingQueryKey });
      toaster.create({
        title: 'Error',
        description: getApiErrorMessage(error, 'Failed to update access settings'),
        type: 'error',
      });
    },
  });
};

export const useUpdateOrganizationSettingsMutation = (orgId: string, { silent = false } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrganizationSettingUpdate) => updateOrganizationSettings(orgId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'settings'],
      });
      if (!silent) {
        toaster.create({
          title: 'Success',
          description: 'Organization settings updated successfully',
          type: 'success',
        });
      }
    },
    onError: (error: unknown) => {
      if (!silent) {
        toaster.create({
          title: 'Error',
          description: getApiErrorMessage(error, 'Failed to update organization settings'),
          type: 'error',
        });
      }
    },
  });
};

export const useUpdateOrganizationDetailsMutation = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrganizationDetailsUpdate) => updateOrganizationDetails(orgId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'details'],
      });
      // The CSM owner is edited here, and the CSM workspace book is scoped by that owner.
      queryClient.invalidateQueries({ queryKey: [CSM_STATE_KEY, 'book'] });
      toaster.create({
        title: 'Success',
        description: 'Organization details updated successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: 'Error',
        description: error?.response?.data?.detail || 'Failed to update organization details',
        type: 'error',
      });
    },
  });
};

const useSpecialHandlingMutation = <TVariables>(
  orgId: string,
  mutationFn: (variables: TVariables) => Promise<unknown>,
  successMessage: string,
  errorMessage: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      toaster.create({ title: 'Success', description: successMessage, type: 'success' });
    },
    onError: (error: unknown) => {
      toaster.create({ title: 'Error', description: getApiErrorMessage(error, errorMessage), type: 'error' });
    },
    // Refresh after a failure too: a 409 means another admin changed special handling first.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'details'] });
      // The reason is a pinned org note, so the org notes list changes with it.
      queryClient.invalidateQueries({ queryKey: [NOTES_STATE_KEY, orgId] });
      // Saving a filer assigns them the org's unassigned open filings. Inactive caches are
      // refetched too: the app turns off refetchOnMount, so a filing page opened later
      // would otherwise show the task without its new assignee.
      queryClient.invalidateQueries({ queryKey: [TASKS_BY_FILING_ID_STATE_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: [TASKS_FILING_LIST_STATE_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: [TASKS_ALL_FILING_STATE_KEY], refetchType: 'all' });
      // The board's special handling count is keyed apart from the board list.
      queryClient.invalidateQueries({ queryKey: [TASKS_FILING_SPECIAL_HANDLING_COUNT_STATE_KEY], refetchType: 'all' });
    },
  });
};

export const useTurnOnSpecialHandlingMutation = (orgId: string) =>
  useSpecialHandlingMutation(
    orgId,
    (payload: SpecialHandlingPayload) => turnOnSpecialHandling(orgId, payload),
    'Special handling turned on',
    'Failed to turn on special handling'
  );

export const useUpdateSpecialHandlingMutation = (orgId: string) =>
  useSpecialHandlingMutation(
    orgId,
    (payload: SpecialHandlingPayload) => updateSpecialHandling(orgId, payload),
    'Special handling updated',
    'Failed to update special handling'
  );

export const useTurnOffSpecialHandlingMutation = (orgId: string) =>
  useSpecialHandlingMutation<void>(
    orgId,
    () => turnOffSpecialHandling(orgId),
    'Special handling turned off',
    'Failed to turn off special handling'
  );

export const useUpdateOrganizationAdminFieldsMutation = (orgId: string, { silent = false } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrganizationAdminUpdate) => updateOrganizationAdminFields(orgId, payload),
    onSuccess: () => {
      // Returned so the mutation stays pending until the refetch lands. Callers then leave edit mode on fresh data.
      const refetched = queryClient.invalidateQueries({
        queryKey: [ORGANIZATIONS_STATE_KEY, orgId],
      });
      if (!silent) {
        toaster.create({
          title: 'Success',
          description: 'Organization fields updated successfully',
          type: 'success',
        });
      }
      return refetched;
    },
    onError: (error: unknown) => {
      if (!silent) {
        toaster.create({
          title: 'Error',
          description: getApiErrorMessage(error, 'Failed to update organization fields'),
          type: 'error',
        });
      }
    },
  });
};
